import { getMerchantId } from "@/lib/company-storage"
import { checkReceivedCoupon, settleReceivedCoupon, type CouponDetails, type CouponReadiness, type CouponTerms } from "@/lib/coupon-readiness"
import { useEffect, useState } from "react"

/** A received code's readiness as the screen shows it, `checking` until the verdict for this exact code arrives. */
export type CouponReadinessView = { status: "checking" } | CouponReadiness

/** A received code as the screen shows it: its readiness and, once read, what it gives and its terms. */
export type ReceivedCouponView = { readiness: CouponReadinessView; details: CouponDetails | null; terms: CouponTerms | null }

/**
 * Checks a code that arrived by link, referral or prize game, files it by the
 * verdict (see `settleReceivedCoupon`) and reads what it gives. Null without a
 * code.
 */
export function useReceivedCoupon(code: string | null): ReceivedCouponView | null {
  const [result, setResult] = useState<{ code: string; view: ReceivedCouponView } | null>(null)

  useEffect(() => {
    const merchantId = getMerchantId()
    if (!code || !merchantId) return
    let cancelled = false
    void checkReceivedCoupon(code, merchantId).then(({ readiness, details, terms }) => {
      if (cancelled) return
      settleReceivedCoupon(code, merchantId, readiness)
      setResult({ code, view: { readiness, details, terms } })
    })
    return () => {
      cancelled = true
    }
  }, [code])

  if (!code) return null
  if (result?.code === code) return result.view
  return { readiness: getMerchantId() ? { status: "checking" } : { status: "unknown" }, details: null, terms: null }
}

/** {@link useReceivedCoupon} when only the verdict matters. */
export function useCouponReadiness(code: string | null): CouponReadinessView | null {
  return useReceivedCoupon(code)?.readiness ?? null
}
